import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { portalApi, fileUrl, formatApiErrorDetail, nivelInfo, ESTADO_PAGO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PortalAsistencia } from "@/components/PortalAsistencia";
import { PortalMensajes } from "@/components/PortalMensajes";
import { PortalPerfil } from "@/components/PortalPerfil";
import { PortalCalendario } from "@/components/PortalCalendario";
import { PortalPlanes } from "@/components/PortalPlanes";
import { AltaHijoDialog } from "@/components/AltaHijoDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LogOut, CreditCard, FileText, Images, Loader2, ExternalLink, CalendarCheck, ClipboardCheck, ChevronDown, User, Users, FileDown, ChevronRight, Inbox } from "lucide-react";
import { LogoBspark } from "@/components/LogoBspark";
import { PieLegal } from "@/components/PieLegal";
import { SelectorIdioma } from "@/components/SelectorIdioma";
import { useIdioma } from "@/context/IdiomaContext";

export function descargarFacturaPdf(f) {
  return portalApi.get(f.pdf, { responseType: "blob" }).then(({ data }) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${f.numero.replace("/", "-")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });
}

const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const SECCIONES = [
  ["perfil", User, "portal.perfil"],
  ["calendario", CalendarCheck, "portal.calendario"],
  ["asistencia", ClipboardCheck, "portal.asistencia"],
  ["galeria", Images, "portal.trabajos"],
  ["pagos", CreditCard, "portal.planes"],
  ["facturas", FileText, "portal.facturas"],
  ["buzon", Inbox, "portal.buzon"],
];

export default function PortalDashboard() {
  const { data, logout, refresh } = usePortalAuth();
  const { t } = useIdioma();
  const navigate = useNavigate();
  const hijos = useMemo(() => data?.hijos || [], [data]);
  const [activo, setActivo] = useState(0);
  const [planes, setPlanes] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [avances, setAvances] = useState([]);
  const [pagando, setPagando] = useState(null);
  const [eleccion, setEleccion] = useState(null);
  const [altaOpen, setAltaOpen] = useState(false);
  const [planPendiente, setPlanPendiente] = useState(null);
  const [avisoHermanos, setAvisoHermanos] = useState("");
  const [urlPago, setUrlPago] = useState("");
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "calendario");
  const [menuAbierto, setMenuAbierto] = useState(false);
  const planEnlace = new URLSearchParams(window.location.search).get("plan");

  const hijo = useMemo(() => hijos[activo], [hijos, activo]);

  useEffect(() => {
    portalApi.get("/portal/facturas").then(({ data }) => setFacturas(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hijo) return;
    portalApi.get(`/portal/alumnos/${hijo.id}/avances`).then(({ data }) => setAvances(data)).catch(() => {});
    if (hijo.sede_id)
      portalApi.get(`/portal/planes?sede_id=${hijo.sede_id}`).then(({ data }) => setPlanes(data)).catch(() => setPlanes([]));
    else setPlanes([]);
  }, [hijo]);

  // enlace directo desde el email de un plan enviado a la familia: abre ya su pago
  useEffect(() => {
    if (!planEnlace || planes.length === 0) return;
    const plan = planes.find((p) => p.id === planEnlace);
    setTab("pagos");
    if (plan) pagar(plan);
    window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planEnlace, planes]);

  const pagar = async (plan) => {
    const planId = plan.id ?? plan;
    const cubre = plan.hermanos || 1;
    const activos = hijos.filter((h) => h.estado === "activo").length;
    if (cubre > activos) {
      setPlanPendiente(plan);
      setAvisoHermanos(
        `Esta cuota cubre ${cubre} hermanos. Registra ${cubre - activos} hijo/a más para contratarla.`
      );
      setAltaOpen(true);
      return;
    }
    // hay que elegir día y hora antes de pagar
    const incluidos = [hijo, ...hijos.filter((h) => h.id !== hijo.id && h.estado === "activo")]
      .slice(0, cubre);
    const { data: turnos } = await portalApi.get("/portal/turnos", { params: { sede_id: hijo.sede_id } });
    setEleccion({ plan, incluidos, turnos, seleccion: {} });
  };

  const irAlResumen = async () => {
    const { plan, incluidos, seleccion } = eleccion;
    const dias = plan.dias_semana || 1;
    for (const al of incluidos) {
      if ((seleccion[al.id] || []).length !== dias) {
        toast.error(`Elige ${dias} turno(s) para ${al.nombre}`);
        return;
      }
    }
    try {
      const { data } = await portalApi.get("/portal/checkout/resumen", {
        params: { plan_id: plan.id, alumno_id: hijo.id },
      });
      const horarios = incluidos.flatMap((al) => (seleccion[al.id] || []).map((sel) => {
        const t = eleccion.turnos.find((x) => x.clase_id === sel.clase_id && x.dia === sel.dia) || sel;
        return {
          alumno: al.nombre, dia: t.dia, hora_inicio: t.hora_inicio, hora_fin: t.hora_fin,
          disciplina: t.disciplina || t.clase || "",
        };
      }));
      setEleccion((e) => ({ ...e, resumen: { ...data, horarios }, quiereSeguro: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmarTurnos = async () => {
    const { plan, incluidos, seleccion } = eleccion;
    if (!eleccion.acepta) {
      toast.error("Debes aceptar las condiciones de la suscripción y la política de cancelación");
      return;
    }
    const dias = plan.dias_semana || 1;
    const horarios = [];
    for (const al of incluidos) {
      const suyos = seleccion[al.id] || [];
      if (suyos.length !== dias) {
        toast.error(`Elige ${dias} turno(s) para ${al.nombre}`);
        return;
      }
      suyos.forEach((s) => horarios.push({ ...s, alumno_id: al.id }));
    }
    setPagando(plan.id);
    // Safari bloquea window.open dentro de una promesa: la pestaña se abre YA, antes de la llamada
    const enIframe = window.self !== window.top;
    const ventana = enIframe ? window.open("", "_blank") : null;
    try {
      const { data } = await portalApi.post("/portal/checkout", {
        plan_id: plan.id, alumno_id: hijo.id, origin_url: window.location.origin, horarios,
        quiere_seguro: !!eleccion.quiereSeguro,
        quiere_block: !!eleccion.quiereBlock,
        acepta_condiciones: !!eleccion.acepta,
      });
      // Stripe no permite cargarse dentro de un iframe (vista previa): abrimos pestaña nueva
      if (!enIframe) {
        window.location.href = data.checkout_url;
        return;
      }
      if (ventana) {
        ventana.location.href = data.checkout_url;
      } else {
        setUrlPago(data.checkout_url);  // el navegador bloqueó la pestaña: enlace manual
      }
      setPagando(null);
    } catch (e) {
      ventana?.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPagando(null);
    }
  };

  const alternarTurno = (alumnoId, turno) => {
    const dias = eleccion.plan.dias_semana || 1;
    const actuales = eleccion.seleccion[alumnoId] || [];
    const clave = (x) => `${x.clase_id}|${x.dia}`;
    const ya = actuales.some((x) => clave(x) === clave(turno));
    let nuevos = ya ? actuales.filter((x) => clave(x) !== clave(turno))
      : [...actuales, { clase_id: turno.clase_id, dia: turno.dia }];
    if (nuevos.length > dias) nuevos = nuevos.slice(-dias);
    setEleccion({ ...eleccion, seleccion: { ...eleccion.seleccion, [alumnoId]: nuevos } });
  };

  if (!hijo)
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <p className="text-muted-foreground">No hay alumnos asociados a tu cuenta.</p>
      </div>
    );

  const pago = ESTADO_PAGO[hijo.estado_pago] || ESTADO_PAGO.pendiente;
  const nivel = nivelInfo(hijo.nivel);
  const hermanosEnCuota = hijo.stripe_subscription_id
    ? hijos.filter((h) => h.stripe_subscription_id === hijo.stripe_subscription_id).length
    : 1;

  return (
    <div className="min-h-screen bg-background" data-testid="portal-dashboard">
      <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoBspark className="h-14 w-14" texto={false} />
            <div>
              <p className="font-display font-extrabold leading-none">FamilyHub</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("portal.areaFamilias")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SelectorIdioma className="hidden sm:inline-flex" />
            <span className="text-sm text-muted-foreground hidden sm:block">{data?.padre?.nombre}</span>
            <Button variant="outline" size="sm" className="rounded-lg"
              onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} orientation="vertical"
        className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col md:flex-row md:gap-10 items-start">
        <aside className="w-full md:w-64 md:shrink-0 md:sticky md:top-24 mb-6 md:mb-0">
          <p className="hidden md:block text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3 px-2">
            {t("portal.menu")}
          </p>
          <button type="button" onClick={() => setMenuAbierto((v) => !v)}
            data-testid="portal-menu-toggle" aria-expanded={menuAbierto}
            className="md:hidden w-full flex items-center gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3.5 font-semibold">
            {(() => { const I = (SECCIONES.find((s) => s[0] === tab) || SECCIONES[0])[1]; return <I className="h-4 w-4 text-primary" />; })()}
            <span>{t((SECCIONES.find((s) => s[0] === tab) || SECCIONES[0])[2])}</span>
            <ChevronDown className={`h-4 w-4 ml-auto transition-transform duration-200 ${menuAbierto ? "rotate-180" : ""}`} />
          </button>
          <TabsList data-testid="portal-menu"
            className={`${menuAbierto ? "flex" : "hidden"} md:flex w-full h-auto bg-muted/40 p-2 rounded-2xl flex-col gap-1 mt-2 md:mt-0 justify-start`}>
            {SECCIONES.map(([valor, Icono, clave]) => (
              <TabsTrigger key={valor} value={valor} data-testid={`portal-tab-${valor}`}
                onClick={() => setMenuAbierto(false)}
                className="w-full justify-start gap-2.5 rounded-xl px-3.5 py-2.5 shrink-0 font-semibold
                  data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary">
                <Icono className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">{t(clave)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </aside>

        <main className="flex-1 min-w-0 w-full">
        {hijos.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {hijos.map((h, i) => (
              <button key={h.id} onClick={() => setActivo(i)} data-testid={`portal-hijo-tab-${h.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                  i === activo ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"
                }`}>
                {h.nombre}
              </button>
            ))}
          </div>
        )}

        <div className="fade-up mb-10">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{hijo.nombre} {hijo.apellidos}</h1>
            <Badge variant="outline" className="rounded-full gap-1.5" data-testid="portal-nivel-badge">
              <span className={`h-2 w-2 rounded-full ${nivel.dot}`} /> {nivel.label}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-2">
            {t("portal.matriculaEtq")} <b className="text-foreground">{hijo.matricula}</b> · {hijo.sede || t("portal.sinSede")}
          </p>
        </div>

        {urlPago && (
          <div className="mb-8 rounded-2xl border border-primary/40 bg-primary/10 p-5" data-testid="portal-enlace-pago">
            <p className="font-display font-bold">Abre el pago en una pestaña nueva</p>
            <p className="text-sm text-muted-foreground mt-1">
              La pasarela de Stripe no puede mostrarse dentro de esta vista previa.
            </p>
            <a href={urlPago} target="_blank" rel="noopener noreferrer"
              className="inline-flex mt-4 px-5 py-2 rounded-full bg-primary text-primary-foreground font-semibold text-sm"
              data-testid="portal-abrir-pago">Ir a la pasarela de pago</a>
          </div>
        )}

        {hijo.estado_pago === "impago" && (
          <div className="mb-8 rounded-2xl border border-destructive/40 bg-destructive/10 p-5"
            data-testid="portal-aviso-impago">
            <p className="font-display font-bold text-destructive">{t("portal.impagoTitulo")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("portal.impagoTexto")}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="portal-card-pago">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t("portal.estadoPago")}</p>
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold border ${pago.cls}`} data-testid="portal-estado-pago">
              {pago.label}
            </span>
            <p className="text-sm text-muted-foreground mt-4 capitalize">
              {hijo.tipo_suscripcion && hijo.importe
                ? `Suscripción ${hijo.tipo_suscripcion} · ${hijo.importe} €${hermanosEnCuota > 1 ? ` (cuota conjunta de ${hermanosEnCuota} hermanos)` : ""}`
                : t("portal.sinSuscripcion")}
            </p>
            {hijo.inscripcion_hasta && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="portal-periodo-pagado">
                {t("portal.pagadoHasta")} {hijo.inscripcion_hasta}
              </p>
            )}
          </Card>
          <Card className="rounded-2xl p-6 border-border shadow-sm">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t("portal.seguro")}</p>
            <Badge variant={hijo.seguro_reposicion ? "default" : "secondary"}>
              {hijo.seguro_reposicion ? t("portal.contratado") : t("portal.noContratado")}
            </Badge>
            <p className="text-sm text-muted-foreground mt-4">
              {hijo.seguro_reposicion ? t("portal.seguroSi") : t("portal.seguroNo")}
            </p>
          </Card>
          {(hijo.clases_disponibles > 0 || hijo.bono_hasta) ? (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="portal-card-bono">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t("portal.bono")}</p>
              <p className="font-display text-4xl font-extrabold">{hijo.clases_disponibles}</p>
              {hijo.bono_hasta && (
                <p className="text-xs text-muted-foreground mt-3">Caduca el {hijo.bono_hasta}</p>
              )}
            </Card>
          ) : hijo.clases_mes > 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="portal-card-clases-mes">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                {t("portal.clasesCuota")}
              </p>
              <p className="font-display text-4xl font-extrabold">{hijo.clases_mes}<span className="text-lg font-bold">/mes</span></p>
              <p className="text-xs text-muted-foreground mt-3">
                {hijo.dias_semana > 1 ? `${hijo.dias_semana} ${t("portal.diasSemana")}` : t("portal.diaSemana")}
              </p>
            </Card>
          )}
        </div>

        {tab !== "pagos" && (
          <button onClick={() => setTab("pagos")} data-testid="portal-cta-planes"
            className="w-full mb-8 rounded-2xl bg-primary text-primary-foreground px-6 py-5 text-left
              shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30
              hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-4">
            <span className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <CreditCard className="h-6 w-6" />
            </span>
            <span className="min-w-0">
              <span className="block font-display text-lg md:text-xl font-extrabold leading-tight">
                {t("portal.pagos")}
              </span>
              <span className="block text-sm opacity-90 mt-0.5">{t("portal.planesCta")}</span>
            </span>
            <ChevronRight className="h-6 w-6 ml-auto shrink-0" />
          </button>
        )}

        <div>
        </div>

          <TabsContent value="calendario" className="pt-8">
            <PortalCalendario hijo={hijo} />
          </TabsContent>

          <TabsContent value="perfil" className="pt-8">
            <PortalPerfil padre={data?.padre} hijos={hijos} onGuardado={refresh}
              onAnadirHijo={() => { setPlanPendiente(null); setAvisoHermanos(""); setAltaOpen(true); }} />
          </TabsContent>

          <TabsContent value="pagos" className="pt-8">
            <div className="mb-6">
              <h2 className="font-display text-2xl font-extrabold">{t("portal.pagos")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("portal.planesCta")}</p>
            </div>
            <PortalPlanes planes={planes} pagando={pagando} onPagar={pagar} />
          </TabsContent>

          <TabsContent value="asistencia" className="pt-8">
            <PortalAsistencia hijo={hijo} />
          </TabsContent>

          <TabsContent value="buzon" className="pt-8">
            <PortalMensajes />
          </TabsContent>

          <TabsContent value="facturas" className="pt-8">
            <div className="space-y-3">
              {facturas.map((f) => (
                <Card key={f.id} className="rounded-2xl p-5 border-border shadow-sm flex items-center justify-between gap-4"
                  data-testid={`portal-factura-${f.id}`}>
                  <div className="flex-1">
                    <p className="font-semibold">{f.concepto || "Pago"}</p>
                    {(f.lineas || []).length > 0 && (
                      <ul className="mt-2 space-y-0.5" data-testid={`portal-factura-lineas-${f.id}`}>
                        {f.lineas.map((ln, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex justify-between max-w-xs">
                            <span>{ln.concepto}</span>
                            <span className="font-semibold">{ln.importe} €</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {f.fecha} · nº {f.numero}
                      {f.alumnos ? ` · ${f.alumnos}` : ""}
                      {f.exenta ? " · exenta de IVA" : ` · IVA ${f.iva_pct}%`}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-display font-bold">{f.importe} €</span>
                    {f.pdf && (
                      <button onClick={() => descargarFacturaPdf(f)} className="text-primary"
                        data-testid={`portal-factura-pdf-${f.id}`} title="Descargar PDF">
                        <FileDown className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Card>
              ))}
              {facturas.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="portal-facturas-empty">
                  Todavía no tienes facturas disponibles.
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="galeria" className="pt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {avances.map((a) => (
                <Card key={a.id} className="rounded-2xl overflow-hidden border-border shadow-sm group"
                  data-testid={`portal-avance-${a.id}`}>
                  {a.file_id && (
                    <div className="relative overflow-hidden h-56">
                      <img src={fileUrl(a.file_id, true)} alt={a.titulo}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-5">
                    <p className="font-display font-bold">{a.titulo || "Avance"}</p>
                    {a.comentario && <p className="text-sm text-muted-foreground mt-2">{a.comentario}</p>}
                    <p className="text-xs text-muted-foreground mt-4">{a.autor} · {(a.created_at || "").slice(0, 10)}</p>
                  </div>
                </Card>
              ))}
              {avances.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="portal-avances-empty">
                  Aún no hay trabajos publicados.
                </p>
              )}
            </div>
          </TabsContent>

        <AltaHijoDialog
          open={altaOpen}
          onOpenChange={setAltaOpen}
          sedeSugerida={hijo?.sede_id}
          titulo={planPendiente ? "Registra a su hermano/a" : "Añadir hijo/a"}
          descripcion={avisoHermanos || undefined}
          onCreado={async () => {
            const nuevo = await refresh();
            const pendiente = planPendiente;
            setPlanPendiente(null);
            setAvisoHermanos("");
            if (pendiente && (nuevo?.hijos?.length || 0) >= (pendiente.hermanos || 1)) {
              toast.success("¡Listo! Ya puedes contratar la cuota de hermanos.");
            }
          }}
        />
      </main>
      </Tabs>
      <Dialog open={!!eleccion} onOpenChange={(v) => !v && setEleccion(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="turnos-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {eleccion?.resumen ? "Revisa tu plan" : "Elige día y hora"}
            </DialogTitle>
            <DialogDescription>
              {eleccion?.resumen
                ? "Esto es lo que incluye tu plan. Si todo está bien, continúa al pago seguro."
                : "Selecciona el turno semanal en el que quieres apuntar a cada alumno/a."}
            </DialogDescription>
          </DialogHeader>
          {eleccion && !eleccion.resumen && (
            <div className="space-y-8">
              <p className="text-sm text-muted-foreground">
                {eleccion.plan.nombre} · {(eleccion.plan.dias_semana || 1)} turno(s) por semana y alumno.
              </p>
              {eleccion.incluidos.map((al) => (
                <div key={al.id} data-testid={`turnos-alumno-${al.id}`}>
                  <p className="font-display font-bold mb-3">{al.nombre}</p>
                  {DIAS_SEMANA.filter((dia) => eleccion.turnos.some((t) => t.dia === dia)).map((dia) => (
                    <div key={dia} className="mb-5">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{dia}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {eleccion.turnos.filter((t) => t.dia === dia).map((tn) => {
                          const elegido = (eleccion.seleccion[al.id] || [])
                            .some((x) => x.clase_id === tn.clase_id && x.dia === tn.dia);
                          const lleno = tn.plazas_libres <= 0;
                          return (
                            <button key={`${al.id}-${tn.clase_id}-${tn.dia}`} disabled={lleno}
                              onClick={() => alternarTurno(al.id, tn)}
                              data-testid={`turno-${al.id}-${tn.clase_id}-${tn.dia}`}
                              className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors duration-200 ${
                                elegido ? "border-primary bg-accent"
                                  : lleno ? "border-border opacity-40 cursor-not-allowed"
                                    : "border-border hover:border-primary"}`}>
                              <span className="font-semibold">{tn.hora_inicio}–{tn.hora_fin}</span>
                              <span className="block text-xs text-muted-foreground mt-0.5">
                                {tn.disciplina || tn.clase} · {lleno ? "Completo" : `${tn.plazas_libres} plazas libres`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {eleccion?.resumen && (
            <div className="space-y-5" data-testid="resumen-checkout">
              <div className="rounded-xl border border-border divide-y divide-border">
                {eleccion.resumen.lineas.map((l) => (
                  <div key={l.concepto} className="flex items-start justify-between gap-4 px-4 py-3">
                    <div>
                      <p className="font-semibold text-sm">{l.concepto}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{l.detalle}</p>
                    </div>
                    <p className="font-bold text-sm shrink-0">{l.importe.toFixed(2)} €</p>
                  </div>
                ))}
              </div>
              {eleccion.resumen.horarios?.length > 0 && (
                <div className="rounded-xl border border-border p-4" data-testid="resumen-horarios">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    Horarios elegidos
                  </p>
                  {eleccion.resumen.horarios.map((h, i) => (
                    <p key={i} className="text-sm">
                      <b>{h.alumno}</b> · {h.dia} {h.hora_inicio}–{h.hora_fin}
                      {h.disciplina ? <span className="text-muted-foreground"> · {h.disciplina}</span> : null}
                    </p>
                  ))}
                </div>
              )}
              {!eleccion.resumen.matricula.aplica && eleccion.resumen.matricula.importe > 0 && (
                <p className="text-xs text-emerald-600 font-semibold" data-testid="matricula-ya-pagada">
                  No pagas matrícula: ya está al día
                  {eleccion.resumen.matricula.vigente_hasta ? ` hasta el ${eleccion.resumen.matricula.vigente_hasta}` : ""}.
                </p>
              )}
              {eleccion.resumen.seguro.disponible && !eleccion.resumen.seguro.vigente && (
                <button onClick={() => setEleccion((e) => ({ ...e, quiereSeguro: !e.quiereSeguro }))}
                  data-testid="seguro-opcional-toggle"
                  className={`w-full text-left rounded-xl border px-4 py-4 transition-colors duration-200 ${
                    eleccion.quiereSeguro ? "border-primary bg-accent" : "border-border hover:border-primary"}`}>
                  <p className="font-display font-bold text-sm">
                    ¿Quieres añadir el seguro de recuperación de clases? · +{eleccion.resumen.seguro.importe.toFixed(2)} €/año
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Permite recuperar las clases que se pierdan avisando con 24 h de antelación.
                    Puedes contratarlo más adelante cuando quieras.
                  </p>
                  <p className="text-xs font-semibold text-primary mt-2">
                    {eleccion.quiereSeguro ? "Sí, lo añado a este pago" : "No, gracias — pulsa para añadirlo"}
                  </p>
                </button>
              )}
              {eleccion.resumen.seguro.vigente && (
                <p className="text-xs text-emerald-600 font-semibold" data-testid="seguro-ya-vigente">
                  Ya tienes el seguro de recuperación de clases
                  {eleccion.resumen.seguro.vigente_hasta ? ` hasta el ${eleccion.resumen.seguro.vigente_hasta}` : ""}.
                </p>
              )}
              {eleccion.resumen.block?.disponible && !eleccion.resumen.block.vigente && (
                <button onClick={() => setEleccion((e) => ({ ...e, quiereBlock: !e.quiereBlock }))}
                  data-testid="block-opcional-toggle"
                  className={`w-full text-left rounded-xl border px-4 py-4 transition-colors duration-200 ${
                    eleccion.quiereBlock ? "border-primary bg-accent" : "border-border hover:border-primary"}`}>
                  <p className="font-display font-bold text-sm">
                    ¿Quieres añadir el block de dibujo? · +{eleccion.resumen.block.importe.toFixed(2)} €
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    El material propio del alumno para trabajar en clase y en casa.
                    Puedes añadirlo más adelante cuando quieras.
                  </p>
                  <p className="text-xs font-semibold text-primary mt-2">
                    {eleccion.quiereBlock ? "Sí, lo añado a este pago" : "No, gracias — pulsa para añadirlo"}
                  </p>
                </button>
              )}
              {eleccion.resumen.block?.vigente && (
                <p className="text-xs text-emerald-600 font-semibold" data-testid="block-ya-incluido">
                  Ya tienes el block de dibujo de este curso.
                </p>
              )}
              <div className="flex items-center justify-between border-t border-border pt-4">
                <p className="font-display font-bold">Total ahora</p>
                <p className="font-display text-2xl font-extrabold" data-testid="resumen-total">
                  {(eleccion.resumen.total
                    + (eleccion.quiereSeguro ? eleccion.resumen.seguro.importe : 0)
                    + (eleccion.quiereBlock ? (eleccion.resumen.block?.importe || 0) : 0)).toFixed(2)} €
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col gap-4 sm:flex-col sm:items-stretch">
            {eleccion?.resumen && (
              <label className="flex items-start gap-3 text-left text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary"
                  checked={!!eleccion?.acepta}
                  onChange={(e) => setEleccion((x) => ({ ...x, acepta: e.target.checked }))}
                  data-testid="acepta-condiciones-check" />
                <span>
                  Estoy de acuerdo con las condiciones de la suscripción y con la{" "}
                  <a href="/legal/terminos" target="_blank" rel="noopener noreferrer"
                    className="text-primary font-semibold underline">política de cancelación</a>.
                </span>
              </label>
            )}
            {eleccion?.resumen ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" className="rounded-full font-semibold"
                  onClick={() => setEleccion((e) => ({ ...e, resumen: null }))}
                  data-testid="volver-turnos-btn">
                  Cambiar día y hora
                </Button>
                <Button className="rounded-full font-semibold flex-1" onClick={confirmarTurnos}
                  disabled={!!pagando} data-testid="confirmar-turnos-btn">
                  {pagando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ir al pago seguro"}
                </Button>
              </div>
            ) : (
              <Button className="rounded-full font-semibold" onClick={irAlResumen}
                data-testid="ver-resumen-btn">
                Continuar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PieLegal />
    </div>
  );
}
